import { useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { PAGES, ChainsDictionary } from "@/config";
import { useAmlCheckStore, usePaymentStore } from "@/store";
import { NetworkInfo, Button, Layout, StateInfoBox } from "@/components";
import { getIconByNetwork } from "@/helpers";
import styles from "./Checkout.module.sass";
import { AddressShorter } from "../AddressShorter/AddressShorter";

const Checkout = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { externalData, token } = useAmlCheckStore();
  const cost = usePaymentStore((state) => state.paymentIntent?.cost);

  const performPayment = useCallback(async () => {
    if (!stripe || !elements) return;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${PAGES.POST_PAYMENT}?token=${token}`,
      },
      redirect: "if_required",
    });

    if (error) {
      console.error(error);
      setErrorMessage(error.message ?? "Payment error");
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      navigate(
        `${PAGES.POST_PAYMENT}?payment_intent=${paymentIntent.id}&payment_intent_client_secret=${paymentIntent.client_secret}&token=${token}&redirect_status=succeeded`,
      );
    }
  }, [stripe, elements, token, navigate]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent | React.MouseEvent) => {
      if (event) event.preventDefault();

      if (!stripe || !elements) return;

      setIsProcessing(true);
      setErrorMessage(null);

      const { error: submitError } = await elements.submit();

      if (submitError) {
        setErrorMessage(submitError.message ?? "Validation error");
        setIsProcessing(false);
        return;
      }

      await performPayment();
    },
    [stripe, elements, performPayment],
  );

  const handleExpressConfirm = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    await performPayment();
  }, [stripe, elements, performPayment]);

  const network = (externalData?.network || "").toUpperCase();

  const title = useMemo(() => {
    const chain = ChainsDictionary[network];
    return chain?.name || "";
  }, [network]);

  const footerButton = (
    <Button
      type="submit"
      onClick={handleSubmit}
      disabled={!stripe || !elements || isProcessing}
      className={styles.button}
      style={{
        cursor: isProcessing ? "not-allowed" : "pointer",
        width: "100%",
      }}
      text={isProcessing ? "Processing..." : `Pay $${cost}`}
    />
  );

  return (
    <Layout
      title="You Checking"
      isShowBackButton
      onBack={() => navigate(PAGES.AML_HISTORY)}
      renderFooter={footerButton}
    >
      <div className={styles.checkout}>
        <div className={styles.head}>
          <NetworkInfo
            icon={getIconByNetwork(network)}
            title={title}
          />

          {!!externalData?.hash && (<AddressShorter address={externalData?.hash} />)}
        </div>

        <ExpressCheckoutElement onConfirm={handleExpressConfirm} />

        <form onSubmit={handleSubmit}>
          <div className={styles.payment}>
            <div className={styles.title}>
              {`To continue, please buy 1 AML check per $${cost}`}
            </div>

            <PaymentElement
              options={{
                layout: "tabs",
              }}
            />
          </div>

          {!!errorMessage && <StateInfoBox type="error" title={errorMessage} className={styles.errorMessage} />}
        </form>

        <style>{`
          .divider {
              display: flex; align-items: center; text-align: center;
              margin: 20px 0; color: #888; font-size: 14px;
          }
          .divider::before, .divider::after {
              content: ''; flex: 1; border-bottom: 1px solid #ddd;
          }
          .divider::before { margin-right: .5em; }
          .divider::after { margin-left: .5em; }
        `}</style>
      </div>
    </Layout>
  );
};

export default Checkout;
